import {
  AfterViewInit,
  ComponentFactory,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges,
  ViewContainerRef,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { FormField } from '@angular/forms/signals';

import { Store } from '@ngrx/store';

import {
  HelpActions,
  HelpPathRegistrar,
  HelpState,
} from '@ups/xplat/base/in-app-help';
import { BaseComponent } from '@ups/xplat/core';

import { HelpComponent } from '../components';
import { HelpAnchorState } from '../services/help-anchor-state.service';

export const attributeIdName = 'help-id'; // attribute that is set with calculated id
export const attributePathName = 'help-path'; // attribute that is set with calculated path
export const idAttributes = ['help-id', 'help-anchor', 'id', 'name']; // attributes that can set help-id, sorted by priority.

/**
 * The help id for an anchored control: the first of help-id, help-anchor, id,
 * name that is set, then (signal forms only) the field's own key.
 *
 * A [formField] control cannot carry a static name (NG8022); Angular writes
 * `<appId>.form<N>.<path>` into it instead, where N is a global form counter,
 * so that name is unstable across page loads and is skipped. The last path
 * segment (e.g. `email`) is stable and is what `name="email"` supplied when
 * the same control used ngModel.
 */
export function resolveHelpId(
  getAttribute: (name: string) => string | null,
  formFieldName?: string | null
): string | null {
  const isFormField = formFieldName != null;
  const fromAttributes = idAttributes
    .filter((attr) => !(isFormField && attr === 'name'))
    .reduce<string | null>((found, attr) => found || getAttribute(attr), null);
  if (fromAttributes) return fromAttributes;
  if (!formFieldName) return null;
  return formFieldName.split('.').pop() || null;
}

@Directive({
  // [formField]: signal-forms controls anchor like ngModel ones did (they
  // silently lost their anchors when converted off ngModel).
  selector: '[help-anchor], [ngModel], [formField]',
  standalone: false,
})
export class HelpAnchorDirective
  extends BaseComponent
  implements AfterViewInit, OnChanges, OnInit
{
  // Declaring these inputs means a BOUND id/name ([id], id="{{...}}") is
  // delivered here instead of to the DOM; ngOnChanges writes it back.
  @Input() id?: string;
  @Input() name?: string;
  // Bug 36097: a per-item anchor ([help-anchor]="..." or help-anchor="{{ ... }}") reaches the
  // directive only through this input; ngOnChanges writes it to the DOM like id and name.
  // [attr.help-anchor] never matches the selector, so it stays inert (the OM guard rejects it).
  @Input('help-anchor') helpAnchor?: string;

  // protected canInject = false;
  protected componentFactory: ComponentFactory<HelpComponent>;

  // Set once ngOnInit has written help-id/help-path, so a LATER help-anchor change recomputes them.
  private initialized = false;
  // An author's static help-id; null when the help-id attribute is the one this directive wrote.
  private authorHelpId: string | null = null;
  private helpComponentRef?: ComponentRef<HelpComponent>;
  // The registered help path; set in ngOnInit, moved by reanchor.
  private readonly path = signal<string | null>(null);
  private readonly anchorState = inject(HelpAnchorState);
  private readonly registrar = inject(HelpPathRegistrar);

  // Present only on a signal-forms control: its field name is the id fallback.
  private readonly formField = inject(FormField, {
    optional: true,
    self: true,
  });

  constructor(
    componentFactoryResolver: ComponentFactoryResolver,
    private viewContainerRef: ViewContainerRef,
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private store: Store
  ) {
    super();
    this.componentFactory =
      componentFactoryResolver.resolveComponentFactory(HelpComponent);

    // Create the lightbulb control only while this path has a tooltip to show (Bug 35599).
    // An always-present sibling cost a view per anchor and broke sibling CSS
    // (Bootstrap .input-group > :not(:last-child), :last-child, +).
    effect(() => {
      const path = this.path();
      const show =
        !!path &&
        this.anchorState.showTooltips() &&
        this.anchorState.hasTooltip(path);
      untracked(() =>
        show ? this.createHelpControl(path) : this.removeHelpControl()
      );
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only write a value that was actually bound: removing on null would strip
    // attributes set by others (e.g. the name Angular writes on [formField]).
    const el = this.elementRef.nativeElement;
    for (const [input, attr] of [
      ['id', 'id'],
      ['name', 'name'],
      ['helpAnchor', 'help-anchor'],
    ] as const) {
      const value = changes[input]?.currentValue;
      if (value != null && value !== '')
        this.renderer.setAttribute(el, attr, `${value}`);
    }
    // A positional anchor can change on an element Angular reuses (row 2 becomes row 1 after a
    // remove); without this, help-id, help-path and the registered path keep the old row.
    if (this.initialized && changes['helpAnchor']) this.reanchor();
  }

  ngOnInit(): void {
    // get control id (from the attribute or input name)
    const el = this.elementRef.nativeElement;
    this.authorHelpId = el.getAttribute(attributeIdName);
    this.initialized = true;
    const walkthroughId = this.calculateId();

    // without an id we won't inject control
    if (!walkthroughId) return;

    // NOTE: we'd check if the help-id attribute exists, if not we'd set the calculated one to ensure attribute in al case (currently we don't need it)...

    // get full path
    // calculatePath only returns undefined for an empty id, ruled out above.
    const walkthroughPath = this.calculatePath(el, walkthroughId)!;

    // apply it to the control immediately
    this.renderer.setAttribute(el, attributePathName, walkthroughPath);
    this.renderer.setAttribute(el, attributeIdName, walkthroughId);
    this.path.set(walkthroughPath);
    this.registrar.register(walkthroughPath);
  }

  override ngOnDestroy(): void {
    const path = this.path();
    if (path) this.store.dispatch(HelpActions.deregisterUIPath({ path }));
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    // NOTE: if here, path is already calculated & set
    const el = this.elementRef.nativeElement;
    const walkthroughId = el.getAttribute(attributeIdName);
    const walkthroughPath = el.getAttribute(attributePathName);

    // without a path there is nothing to edit (id must not be provided, we can work without)
    if (!walkthroughPath) return;

    // Touch the inline border only while the editor is open, and put back the author's INLINE
    // value on close. Writing a computed snapshot back inline (the old behaviour, on every load)
    // pinned the border, so :focus and validation border styles stopped applying (Bug 36078).
    let inlineBorder: string | null = null;
    this.store
      .pipe(HelpState.selectEditorState(this.destroy$))
      .subscribe((state) => {
        if (state.show && inlineBorder === null) {
          inlineBorder = el.style.border;
          el.style.border = 'dashed 4px #f5e05055';
        } else if (!state.show && inlineBorder !== null) {
          el.style.border = inlineBorder;
          inlineBorder = null;
        }
      });
  }

  private createHelpControl(walkthroughPath: string): void {
    if (this.helpComponentRef) return;
    const componentRef = this.viewContainerRef.createComponent(
      this.componentFactory
    );
    // moveTo assigns the path and THEN refreshes: the constructor's dictionary subscription
    // already replayed with no path, and that replay carried this path's tooltip (92874/92875).
    componentRef.instance.moveTo(
      this.elementRef.nativeElement.getAttribute(attributeIdName) ?? '',
      walkthroughPath
    );
    componentRef.changeDetectorRef.detectChanges();
    this.helpComponentRef = componentRef;
  }

  private removeHelpControl(): void {
    this.helpComponentRef?.destroy();
    this.helpComponentRef = undefined;
  }

  /** Recompute help-id and help-path after help-anchor changed, and move the lightbulb's path. */
  private reanchor(): void {
    const el = this.elementRef.nativeElement;
    const walkthroughId = this.calculateId();
    if (!walkthroughId) return;
    // Start above the element: its own help-path is the stale one being replaced.
    const walkthroughPath = this.calculatePath(
      el.parentElement ?? el,
      walkthroughId
    )!;
    this.renderer.setAttribute(el, attributePathName, walkthroughPath);
    this.renderer.setAttribute(el, attributeIdName, walkthroughId);
    const previous = this.path();
    if (walkthroughPath !== previous) {
      if (previous)
        this.store.dispatch(HelpActions.deregisterUIPath({ path: previous }));
      this.registrar.register(walkthroughPath);
      this.path.set(walkthroughPath);
    }
    this.helpComponentRef?.instance.moveTo(walkthroughId, walkthroughPath);
  }

  calculateId(): string | null {
    const el = this.elementRef.nativeElement;
    return resolveHelpId(
      (attr) =>
        // the help-id this directive wrote must not outrank a changed help-anchor
        (attr === attributeIdName && this.initialized
          ? this.authorHelpId
          : el.getAttribute(attr)) ||
        (attr === 'id'
          ? this.id
          : attr === 'name'
            ? this.name
            : attr === 'help-anchor'
              ? this.helpAnchor
              : null) ||
        null,
      this.formField ? this.formField.state().name() : null
    );
  }

  calculatePath(
    nativeElement: HTMLElement,
    walkthroughId: string
  ): string | undefined {
    // on empty id, quit, don't consider rest of the hierarchy
    if (!walkthroughId) return;

    // find first element with path attribute
    while (
      nativeElement.parentNode &&
      nativeElement.parentNode !== document &&
      !nativeElement.hasAttribute(attributePathName)
    )
      nativeElement = nativeElement.parentNode as HTMLElement;

    const parentPathElement = nativeElement;
    const parentPathAttribute = parentPathElement
      ? parentPathElement.getAttribute(attributePathName)
      : undefined;

    return parentPathAttribute
      ? parentPathAttribute + '/' + walkthroughId
      : '' + walkthroughId;
    // note: originally we tried to pre-pend the final WT path with a './' to signalize it's an absolute path - but we don't need to do as UI WT paths are always absolute
  }
}
